import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useSetShippingRate } from "@babackapp/return-widget-components/hooks/use-set-shipping-rate";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { useShouldPayReshipping } from "@babackapp/return-widget-components/hooks/use-should-pay-reshipping";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useShippingRates } from "@babackapp/return-widget-components/queries/use-shipping-rates";
import type { FinancialSummary } from "@babackapp/return-widget-components/types";
import type { ShippingRateDto } from "@babackapp/private-api-client/generated/requests/types.gen";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

export type UseCheckoutReturn = {
  financialSummary: FinancialSummary;
  /** amountDue + selected shipping rate, in cents — what the customer will actually pay. */
  totalAmountDue: number;
  shippingRates: ShippingRateDto[];
  selectedRateId: string | null;
  selectRate: (rateId: string) => void;
  canProceed: boolean;
  onProceed: () => void;
  isLoadingRates: boolean;
  ratesError: Error | null;
  checkoutToken: string | null;
  /** True when the customer must pay (amountDue > 102) or pay reshipping. Controls rate selector + Stripe visibility. */
  needsPayment: boolean;
  /** True when payment is only for reshipping (product balance is settled) — shown as a labeled receipt line. */
  shouldPayReshipping: boolean;
  /** Selected shipping rate amount, in cents. */
  shippingRateAmount: number;
};

export function useCheckout() {
  const { orderId, shopId } = useWidgetParams();
  const draft = useDraftReturn(orderId);
  const { data: order } = usePublicOrder(orderId);
  const { data: shop } = usePublicShop(shopId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);
  const shouldPayReshipping = useShouldPayReshipping(orderId, shopId);
  const setShippingRate = useSetShippingRate(orderId);
  const navigate = useNavigate();

  const shippingRatesMutation = useShippingRates();
  const { mutate: fetchShippingRates, status: ratesStatus } =
    shippingRatesMutation;

  // BBS-237 follow-up: financialSummary momentarily recomputes to an empty
  // summary (amountDue: 0) whenever draft.lineItems is transiently empty —
  // e.g. mid-reset after a failed payment retry — which would otherwise
  // swap the Pay button for the no-payment Confirm button and let the
  // customer click straight through to submission with no captured
  // payment. Once a real payment requirement has been observed for this
  // order, keep treating it as required for the rest of the session; only
  // a genuinely payment-free order should ever show the Confirm button.
  const rawNeedsPayment =
    financialSummary.amountDue > 102 || shouldPayReshipping;
  const [prevRawNeedsPayment, setPrevRawNeedsPayment] =
    useState(rawNeedsPayment);
  const [everNeededPayment, setEverNeededPayment] = useState(rawNeedsPayment);
  if (rawNeedsPayment !== prevRawNeedsPayment) {
    setPrevRawNeedsPayment(rawNeedsPayment);
    if (rawNeedsPayment) setEverNeededPayment(true);
  }
  const needsPayment = rawNeedsPayment || everNeededPayment;

  useEffect(() => {
    if (needsPayment && draft && ratesStatus === "idle") {
      fetchShippingRates(draft);
    }
  }, [needsPayment, draft, ratesStatus, fetchShippingRates]);

  const shippingRates: ShippingRateDto[] =
    shippingRatesMutation.data?.shipping_rates ?? [];

  const selectRate = useCallback(
    (rateId: string) => {
      const rate = shippingRates.find((r) => r.handle === rateId);
      const amountCents = rate?.price.amount
        ? Math.round(parseFloat(rate.price.amount) * 100)
        : null;
      setShippingRate(rateId, rate?.title ?? null, amountCents);
    },
    [setShippingRate, shippingRates],
  );

  // Pre-select the first rate so the total/pay button always shows an amount,
  // mirroring the old widget's default rate selection.
  useEffect(() => {
    if (shippingRates.length > 0 && !draft?.shippingRateId) {
      selectRate(shippingRates[0].handle);
    }
  }, [shippingRates, draft?.shippingRateId, selectRate]);

  const onProceed = useCallback(() => {
    navigate("../complete");
  }, [navigate]);

  const selectedRateId = draft?.shippingRateId ?? null;
  const shippingRateAmount = draft?.shippingRateAmount ?? 0;
  const totalAmountDue =
    Math.round(financialSummary.amountDue) + shippingRateAmount;

  const isLoadingRates = shippingRatesMutation.status === "pending";
  const canProceed = needsPayment
    ? !isLoadingRates && (shippingRates.length === 0 || selectedRateId !== null)
    : true;

  return {
    financialSummary,
    totalAmountDue,
    shippingRates,
    selectedRateId,
    selectRate,
    canProceed,
    onProceed,
    isLoadingRates,
    ratesError: shippingRatesMutation.error,
    checkoutToken: shippingRatesMutation.data?.checkout_token ?? null,
    needsPayment,
    shouldPayReshipping,
    shippingRateAmount,
    userPortalTexts: shop?.preferences?.userPortal?.checkoutMessage,
  };
}
